import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui-kit/button";
import { RichTextContent } from "@/components/ui-kit/rich-text-content";

import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { assert } from "../../../utils/utils";
import { AddStudyText } from "./add-study-text";
import { StudyTextsForm } from "./study-texts-form";

export const ConsentsFormRoute = observer(function ConsentsFormRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const params = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const admin = props.app.requireAdminShit();
  const model = admin.studyTexts;
  assert(model, "Study texts model not initialized");
  const countries = useCountries();

  const isAddMode = !params.uuid;

  const selectedItem = isAddMode
    ? null
    : (model.data.find((i) => i.uuid === params.uuid) ?? null);

  const [addFields] = useState(() => new AddStudyText(model));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Texts are append-only: a published text is never edited, a change is a
  // new text at a higher version.
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const uuid = await addFields.post();
      void navigate(`/admin/settings/consents/${uuid}`);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t`Failed to create study text`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const canSave =
    addFields.monitoring_type != null &&
    addFields.study_text_language != null &&
    addFields.study_text.trim().length > 0;

  if (!isAddMode && !selectedItem) {
    return (
      <div>
        <p className="text-danger-foreground">
          <Trans>Study text not found.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 mt-6 bg-card shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">
          {isAddMode
            ? t`Add New Study Text`
            : `${selectedItem?.monitoringTypeText} - ${selectedItem?.languageText}`}
        </h1>
        {isAddMode && (
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave}
            pending={isSaving}
          >
            <Trans>Create</Trans>
          </Button>
        )}
      </div>

      {error && (
        <p className="text-danger-foreground mb-4">
          <Trans>Error: {error}</Trans>
        </p>
      )}

      {isAddMode ? (
        <StudyTextsForm fields={addFields} admin={admin} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Country</Trans>
              </h3>
              <p className="text-foreground">{selectedItem?.country || "—"}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Region</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem && selectedItem?.country && selectedItem?.region
                  ? countries.getRegionSchema(
                      selectedItem?.country,
                      selectedItem?.region,
                    )?.region_name
                  : "—"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Clinic</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem?.clinicName || "—"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Physician</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem?.physicianName || "—"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Version</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem?.study_text_version ?? "—"}
              </p>
            </div>
          </div>
          <div className="border-t pt-2">
            <div className="prose prose-sm max-w-full min-h-40">
              {selectedItem?.study_text ? (
                <RichTextContent value={selectedItem.study_text} />
              ) : (
                <p className="text-muted-foreground">
                  <Trans>No content</Trans>
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
});
